import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const emptyForm = {
  policy_scope: 'platform',
  plan_id: '',
  tenant_id: '',
  policy_name: '',
  request_limit_per_minute: '',
  daily_request_limit: '',
  monthly_request_limit: '',
  concurrent_request_limit: '',
  burst_limit: '',
  status: 'draft',
  enforcement_mode: 'shadow',
  is_platform_default: false
};

function numOrEmpty(s) {
  const t = String(s || '').trim();
  return t === '' ? null : Number(t);
}

export default function PlatformUsagePolicyFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();

  const [form, setForm] = useState(emptyForm);
  const [plans, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ready && user?.role !== 'super_admin') {
      navigate('/platform/usage-policies', { replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pr, tr] = await Promise.all([
          platformApiFetch('/api/platform/plans?pageSize=200&sort=name&order=asc'),
          platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc')
        ]);
        const pj = await pr.json().catch(() => ({}));
        const tj = await tr.json().catch(() => ({}));
        if (!cancelled) {
          if (pr.ok) setPlans(pj.plans || []);
          if (tr.ok) setTenants(tj.tenants || []);
        }
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/usage-policies/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const p = json.policy;
      setForm({
        policy_scope: p.policy_scope || 'platform',
        plan_id: p.plan_id != null ? String(p.plan_id) : '',
        tenant_id: p.tenant_id != null ? String(p.tenant_id) : '',
        policy_name: p.policy_name || '',
        request_limit_per_minute: p.request_limit_per_minute != null ? String(p.request_limit_per_minute) : '',
        daily_request_limit: p.daily_request_limit != null ? String(p.daily_request_limit) : '',
        monthly_request_limit: p.monthly_request_limit != null ? String(p.monthly_request_limit) : '',
        concurrent_request_limit: p.concurrent_request_limit != null ? String(p.concurrent_request_limit) : '',
        burst_limit: p.burst_limit != null ? String(p.burst_limit) : '',
        status: p.status || 'draft',
        enforcement_mode: p.enforcement_mode || 'shadow',
        is_platform_default: Boolean(p.is_platform_default)
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      policy_name: form.policy_name.trim(),
      request_limit_per_minute: numOrEmpty(form.request_limit_per_minute),
      daily_request_limit: numOrEmpty(form.daily_request_limit),
      monthly_request_limit: numOrEmpty(form.monthly_request_limit),
      concurrent_request_limit: numOrEmpty(form.concurrent_request_limit),
      burst_limit: numOrEmpty(form.burst_limit),
      status: form.status,
      enforcement_mode: form.enforcement_mode
    };

    if (!isEdit) {
      body.policy_scope = form.policy_scope;
      if (form.policy_scope === 'plan') {
        body.plan_id = form.plan_id ? Number(form.plan_id) : null;
      }
      if (form.policy_scope === 'tenant_override') {
        body.tenant_id = form.tenant_id ? Number(form.tenant_id) : null;
        body.enforcement_mode = 'inherit';
      }
      if (form.policy_scope === 'platform') {
        body.is_platform_default = form.is_platform_default;
      }
    } else {
      if (form.policy_scope === 'platform') {
        body.is_platform_default = form.is_platform_default;
      }
    }

    try {
      const res = await platformApiFetch(
        isEdit ? `/api/platform/usage-policies/${id}` : '/api/platform/usage-policies',
        {
          method: isEdit ? 'PATCH' : 'POST',
          body
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      navigate(`/platform/usage-policies/${json.policy.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {isEdit ? 'Edit usage policy' : 'New usage policy'}
      </Typography>
      <Button sx={{ mb: 2 }} onClick={() => navigate(isEdit ? `/platform/usage-policies/${id}` : '/platform/usage-policies')}>
        Back
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper component="form" onSubmit={submit} sx={{ p: 3, maxWidth: 720 }}>
        {!isEdit && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel>Policy scope</InputLabel>
              <Select
                label="Policy scope"
                value={form.policy_scope}
                onChange={(e) => set('policy_scope', e.target.value)}
              >
                <MenuItem value="platform">Platform (template)</MenuItem>
                <MenuItem value="plan">Plan</MenuItem>
                <MenuItem value="tenant_override">Tenant override</MenuItem>
              </Select>
            </FormControl>
            {form.policy_scope === 'plan' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Plan</InputLabel>
                <Select
                  label="Plan"
                  value={form.plan_id}
                  onChange={(e) => set('plan_id', e.target.value)}
                  required
                >
                  {plans.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.name} ({p.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {form.policy_scope === 'tenant_override' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Tenant</InputLabel>
                <Select
                  label="Tenant"
                  value={form.tenant_id}
                  onChange={(e) => set('tenant_id', e.target.value)}
                  required
                >
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {t.tenant_name} ({t.tenant_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {form.policy_scope === 'platform' && (
              <FormControlLabel
                sx={{ mt: 1, display: 'block' }}
                control={
                  <Switch
                    checked={form.is_platform_default}
                    onChange={(e) => set('is_platform_default', e.target.checked)}
                  />
                }
                label="Platform default (only one active default should be marked)"
              />
            )}
          </>
        )}

        {isEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Scope and plan/tenant targets cannot be changed here. Create a new policy if you need a different
            attachment.
          </Alert>
        )}

        <TextField
          label="Policy name"
          fullWidth
          required
          margin="normal"
          value={form.policy_name}
          onChange={(e) => set('policy_name', e.target.value)}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)}>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        {form.policy_scope !== 'tenant_override' && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Enforcement</InputLabel>
            <Select
              label="Enforcement"
              value={form.enforcement_mode}
              onChange={(e) => set('enforcement_mode', e.target.value)}
            >
              <MenuItem value="off">Off</MenuItem>
              <MenuItem value="shadow">Shadow (log only)</MenuItem>
              <MenuItem value="enforce">Enforce</MenuItem>
            </Select>
          </FormControl>
        )}

        {isEdit && form.policy_scope === 'platform' && (
          <FormControlLabel
            sx={{ mt: 1, display: 'block' }}
            control={
              <Switch
                checked={form.is_platform_default}
                onChange={(e) => set('is_platform_default', e.target.checked)}
              />
            }
            label="Platform default"
          />
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Limits (leave blank for unlimited)
        </Typography>
        <TextField
          label="Request limit / minute"
          fullWidth
          margin="dense"
          value={form.request_limit_per_minute}
          onChange={(e) => set('request_limit_per_minute', e.target.value)}
        />
        <TextField
          label="Daily request limit"
          fullWidth
          margin="dense"
          value={form.daily_request_limit}
          onChange={(e) => set('daily_request_limit', e.target.value)}
        />
        <TextField
          label="Monthly request limit"
          fullWidth
          margin="dense"
          value={form.monthly_request_limit}
          onChange={(e) => set('monthly_request_limit', e.target.value)}
        />
        <TextField
          label="Concurrent request limit"
          fullWidth
          margin="dense"
          value={form.concurrent_request_limit}
          onChange={(e) => set('concurrent_request_limit', e.target.value)}
        />
        <TextField
          label="Burst limit"
          fullWidth
          margin="dense"
          value={form.burst_limit}
          onChange={(e) => set('burst_limit', e.target.value)}
        />

        <Box sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
